import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/ui/PasswordInput";
import { Gift, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function CustomerRegister() {
  const navigate = useNavigate();
  const { registerCustomer } = useAuth();
  const [form, setForm] = useState({ name: "", whatsapp: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.whatsapp.length < 10) return toast.error("Captura tu celular completo (10 dígitos)");
    if (form.password.length < 4) return toast.error("La contraseña debe tener al menos 4 caracteres");
    setBusy(true);
    try {
      await registerCustomer(form);
      toast.success(`¡Bienvenido, ${form.name}!`);
      navigate("/mi-cuenta");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarte");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="inline-flex items-center gap-2 bg-[#EDE9FE] border border-[#C4B5FD] text-[#5B21B6] text-xs px-3 py-1 rounded-full font-semibold">
          <Gift className="w-3.5 h-3.5" /> Programa de lealtad gratis
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-3">Crea tu cuenta</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Solo tu nombre y tu celular. Acumula puntos en cada compra y canjea recompensas.
        </p>

        <form onSubmit={submit} className="mt-6 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-4" data-testid="customer-register-form">
          <Field label="Tu nombre *">
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="María Pérez" data-testid="cust-name" />
          </Field>
          <Field label="Celular *">
            <div className="relative">
              <Smartphone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                required
                inputMode="numeric"
                className="pl-9"
                value={form.whatsapp}
                onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="5512345678"
                data-testid="cust-whatsapp"
              />
              {form.whatsapp.length > 0 && form.whatsapp.length < 10 && (
                <p className="text-[11px] text-amber-600 mt-1">{form.whatsapp.length}/10 dígitos — faltan {10 - form.whatsapp.length}</p>
              )}
              {form.whatsapp.length === 10 && (
                <p className="text-[11px] text-green-600 mt-1">✓ Número completo</p>
              )}
            </div>
          </Field>
          <Field label="Contraseña sencilla *">
            <PasswordInput required value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Mín. 4 caracteres" data-testid="cust-password" />
            {form.password.length > 0 && form.password.length < 4 && (
              <p className="text-[11px] text-amber-600 mt-1">Faltan {4 - form.password.length} caracteres más</p>
            )}
            {form.password.length >= 4 && (
              <p className="text-[11px] text-green-600 mt-1">✓ Contraseña válida</p>
            )}
          </Field>
          <Button type="submit" disabled={busy} className="w-full btn-primary rounded-full py-6" data-testid="cust-register-submit">
            {busy ? "Creando cuenta…" : "Crear mi cuenta gratis"}
          </Button>
          <div className="text-sm text-center text-muted-foreground">
            ¿Ya tienes cuenta? <Link to="/login-cliente" className="text-[hsl(var(--primary))] font-medium">Inicia sesión</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
